/**
 * Los nombres comerciales de los grupos (PRODUCTO §8.3), sólo para el equipo. El servidor manda el nombre descriptivo de
 * cada grupo (el que nunca ve el comercio) y, para los del diseño, una sugerencia. Lo que se guarda queda en la tabla de
 * la marca: la próxima campaña nace con esos nombres. Las reglas (largo, palabras que no van, que no se repitan) las
 * revisa el servidor y su motivo se muestra en el grupo. Quien lo usa le pone de `key` los nombres guardados: al cambiar,
 * el formulario vuelve a empezar desde ellos.
 *
 * La sugerencia arranca cargada (ahorra escribir), pero se ve como sugerida, no como guardada: el campo punteado y el
 * chip «Sugerido», y el contador dice cuántos faltan guardar (auditoría de diseño del 18/10/2026).
 *
 * El grupo que no tiene nombre ni sugerencia queda con el campo vacío: se marca «Falta el nombre», porque sin él no se
 * sigue a Mensajes y un campo vacío entre otros cargados no se ve (Alan, 01/11/2026).
 */

import { useState, type FormEvent } from "react";
import { AlertTriangle } from "lucide-react";
import { num } from "../../campana-nueva/utils/format";
import type { GrupoDeLaSegmentacion } from "../types";
import { btnPrimario } from "./PasosCampana";

export interface ResultadoNombres {
  ok: boolean;
  error?: string;
  grupoId?: string;
}

export function NombresGrupos({ grupos, guardar }: { grupos: GrupoDeLaSegmentacion[]; guardar: (nombres: Record<string, string>) => Promise<ResultadoNombres> }) {
  const inicial = () => Object.fromEntries(grupos.map((g) => [g.id, g.nombre ?? g.sugerencia ?? ""]));
  const [valores, setValores] = useState<Record<string, string>>(inicial);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<{ texto: string; grupoId?: string } | null>(null);
  const [guardado, setGuardado] = useState(false);

  const faltan = grupos.filter((g) => !g.nombre).length;
  const esSugerido = (g: GrupoDeLaSegmentacion) => !g.nombre && !!g.sugerencia && (valores[g.id] ?? "").trim() === g.sugerencia;
  const sugeridos = grupos.filter(esSugerido).length;
  const falta = (g: GrupoDeLaSegmentacion) => !g.nombre && !(valores[g.id] ?? "").trim();
  const cambios = Object.fromEntries(Object.entries(valores).filter(([id, v]) => v.trim() && v.trim() !== (grupos.find((g) => g.id === id)?.nombre ?? "")));

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setError(null);
    setGuardado(false);
    const r = await guardar(Object.fromEntries(Object.entries(cambios).map(([id, v]) => [id, v.trim()])));
    setGuardando(false);
    if (!r.ok) setError({ texto: r.error || "No pudimos guardar los nombres.", grupoId: r.grupoId });
    else setGuardado(true);
  };

  return (
    <form onSubmit={enviar} aria-labelledby="nombres-titulo" className="space-y-4 rounded-2xl border border-amber-200 bg-amber-50/60 p-5" data-testid="nombres-grupos">
      <div className="space-y-1">
        <h2 id="nombres-titulo" className="text-base font-semibold text-slate-900">
          Nombres de los grupos <span className="ml-1 rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-600">sólo el equipo</span>
        </h2>
        <p className="text-sm text-slate-600">
          {faltan > 0
            ? `Faltan guardar ${faltan === 1 ? "1 nombre" : `${faltan} nombres`}: hasta que estén, el Excel y el PDF no se pueden bajar.${
                sugeridos > 0 ? " Los sugeridos no cuentan hasta que los guardes: revisalos y tocá «Guardar nombres»." : ""
              } Quedan para las próximas campañas de la marca.`
            : "El comercio ve estos nombres en la pantalla, el Excel y el PDF. Quedan para las próximas campañas de la marca."}
        </p>
      </div>
      <ul className="space-y-3">
        {grupos.map((g) => {
          const id = `nombre-grupo-${g.id}`;
          const conError = error?.grupoId === g.id;
          const sugerido = esSugerido(g);
          const sinNombre = falta(g);
          return (
            <li key={g.id} className="grid gap-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:items-center sm:gap-4">
              <label htmlFor={id} className="min-w-0 text-sm">
                <span className="block font-medium text-slate-900">{g.nombreMotor}</span>
                <span className="block text-xs text-slate-500">{num(g.personas)} personas</span>
                {g.terminaEnNumero && (
                  <span className="mt-1 flex items-start gap-1 text-xs text-amber-800">
                    <AlertTriangle size={14} aria-hidden="true" className="mt-px flex-none" />
                    Termina en número: hay dos grupos parecidos. Revisá que el nombre los distinga.
                  </span>
                )}
              </label>
              <div className="flex items-center gap-2">
                <input
                  id={id}
                  value={valores[g.id] ?? ""}
                  maxLength={31}
                  onChange={(e) => setValores((v) => ({ ...v, [g.id]: e.target.value }))}
                  aria-invalid={conError || undefined}
                  aria-describedby={conError ? "nombres-error" : sugerido ? `${id}-sugerido` : sinNombre ? `${id}-falta` : undefined}
                  placeholder="Nombre comercial"
                  className={`min-h-[44px] w-full rounded-xl border px-3 text-sm text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${
                    conError || sinNombre ? "border-red-400 bg-white" : sugerido ? "border-dashed border-violet-300 bg-violet-50/50" : "border-slate-200 bg-white"
                  }`}
                />
                {sugerido && (
                  <span id={`${id}-sugerido`} className="flex-none rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700">
                    Sugerido
                  </span>
                )}
                {sinNombre && (
                  <span id={`${id}-falta`} className="flex-none rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                    Falta el nombre
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {error && (
        <p id="nombres-error" role="alert" className="text-sm font-medium text-red-700">
          {error.texto}
        </p>
      )}
      {guardado && !error && (
        <p role="status" className="text-sm text-emerald-700">
          Nombres guardados.
        </p>
      )}
      <button type="submit" className={btnPrimario} disabled={guardando || Object.keys(cambios).length === 0}>
        {guardando ? "Guardando…" : "Guardar nombres"}
      </button>
    </form>
  );
}
